import type {
    DataTableColumn,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
} from '@/components/ui/data-table-customized'
import { Ticket as TicketType } from '@/modules/Ticket/types/response'
import { TFunction } from 'i18next'

// ============================================================================
// Constants
// ============================================================================

/**
 * Ticket status options with color mapping
 */
export const getTicketStatusOptions = (t: TFunction): OptionItem[] => [
    {
        value: 'opening',
        label: t('status.opening', { defaultValue: 'Mới' }),
        color: 'default',
    },
    {
        value: 'work-in-progress',
        label: t('status.work-in-progress', { defaultValue: 'Đang xử lý' }),
        color: 'info',
    },
    {
        value: 'pending',
        label: t('status.pending', { defaultValue: 'Chờ' }),
        color: 'warning',
    },
    {
        value: 'resolved',
        label: t('status.resolved', { defaultValue: 'Đã xử lý' }),
        color: 'success',
    },
    {
        value: 'closed',
        label: t('status.closed', { defaultValue: 'Đã đóng' }),
        color: 'success',
    },
]

// ============================================================================
// Column Configuration
// ============================================================================

/**
 * Column configuration for Customer Tickets table
 * Compact design for ManageSLA context
 */
export const getCustomerTicketsColumns = (
    t: TFunction
): DataTableColumn<TicketType>[] => [
    {
        key: 'ticket_id',
        title: 'ID',
        width: 80,
        sortable: false,
        formatter: createInternalLinkFormatter({
            pathKey: 'ticketDetail',
            pathParams: (value) => ({ id: value }),
            label: (value) => `DC Ticket - ${value}`,
        }),
    },
    {
        key: 'ticket_type_title',
        title: t('type', { defaultValue: 'Loại' }),
        width: 120,
        sortable: false,
    },
    {
        key: 'service_id',
        title: t('service', { defaultValue: 'Dịch vụ' }),
        width: 80,
        sortable: false,
        formatter: createInternalLinkFormatter({
            pathKey: 'serviceDetail',
            pathParams: (value) => ({ id: value }),
            label: (value, row) =>
                [value, row?.service_name].filter(Boolean).join(' - ') || '-',
        }),
    },
    {
        key: 'ticket_status',
        title: t('status', { defaultValue: 'Trạng thái' }),
        width: 100,
        sortable: false,
        formatter: createOptionsFormatter({
            options: getTicketStatusOptions(t),
        }),
    },
    {
        key: 'ticket_requested_at',
        title: t('created_at'),
        width: 120,
        sortable: false,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'HH:mm DD/MM/YYYY',
            timezone: 'utc',
        }),
    },
    {
        key: 'ticket_technical_assignees',
        title: 'Assignee',
        width: 100,
        sortable: false,
        render(value: { employee_id: number; employee_name: string }[]) {
            return (
                <span className="text-xs text-gray-500 truncate max-w-[100px] block">
                    {value?.map((a) => a.employee_name).join(', ') || '-'}
                </span>
            )
        },
    },
]
